import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HeartPulse, ChevronRight } from 'lucide-react';
import { fetchHealthRecord, clearHealthRecord, HEALTH_CATEGORIES } from '../../redux/healthRecordSlice';

// THE HEALTH RECORD ON THE VISIT SCREEN, folded to one row (round 9):
//   "2 entries this visit · Open health record"
// The full form used to sit here, between the prescription and the uploads,
// and a doctor reading the record had to leave the visit to do it. Now reading
// AND adding happen in the drawer (HealthRecordDrawer) over the visit, and this
// row only says what this visit has added so far.
//
// The row owns the record's lifecycle on the visit screen: it reads it (with
// the open appointment, so `isNew` is computed) and clears it when the visit
// goes away. The drawer reuses the same slice state and does not clear it, so
// the count here stays right after the drawer closes.

function HealthRecordVisitRow({ patientId, appointmentId, onOpen }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { categories, error } = useSelector((state) => state.healthRecord);

    useEffect(() => {
        if (patientId && appointmentId) dispatch(fetchHealthRecord({ patientId, appointmentId }));
        return () => { dispatch(clearHealthRecord()); };
    }, [dispatch, patientId, appointmentId]);

    // Entries written inside THIS visit — retracted ones do not count.
    const thisVisit = useMemo(
        () => HEALTH_CATEGORIES.reduce((n, c) => n + (categories?.[c] || [])
            .filter((e) => appointmentId && String(e.appointmentId) === String(appointmentId) && e.status !== 'incorrect').length, 0),
        [categories, appointmentId]
    );

    return (
        <div
            data-testid="health-visit-row"
            className="mb-4 flex w-full flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-r from-[#4ABDBD]/10 to-transparent px-4 py-3 sm:mb-6"
        >
            <span className="flex items-center gap-2 text-sm font-semibold text-[#2D8A8A]">
                <HeartPulse className="h-5 w-5" aria-hidden="true" />
                {t('healthRecord.add.section_title')}
                {/* The count is unknown when the read failed; say nothing
                    rather than "0 entries". */}
                {!error && (
                    <span data-testid="health-visit-row-count" className="font-normal text-gray-600">
                        · {t('healthRecord.drawer.visit_count', { count: thisVisit })}
                    </span>
                )}
            </span>
            <button
                type="button"
                data-testid="health-visit-row-open"
                disabled={!patientId || !appointmentId}
                onClick={onOpen}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-1.5 text-sm font-semibold text-[#2D8A8A] transition-colors hover:bg-[#4ABDBD]/15 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
                {t('healthRecord.drawer.open_link', 'Open health record')}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    );
}

export default HealthRecordVisitRow;
